import { parseIsoDateToMDYUS, toIsoDate } from "./dateUtils";
import { mergeFormDataWithStatusExtras } from "./applicationFormPayload";

/** Matches backend GROUP_1_ROLES (supervisor + first patient care date). */
export const WELCOME_LETTER_GROUP_1 = ["RN", "LVN", "HHA", "MSW"];

/** Matches backend GROUP_2_ROLES (contractor agreement JSON). */
export const WELCOME_LETTER_GROUP_2 = ["PT", "OT", "ST", "PTA", "OTA"];

const PAY_RATE_KEYS = [
  "initial_assessment_oasis",
  "recertification",
  "resumption",
  "discharge",
  "regular_visit",
  "iv_infusion",
  "blood_draw",
  "initial_assessment",
];

export const isWelcomeLetterGroup1 = (role) =>
  WELCOME_LETTER_GROUP_1.includes(role);

export const isWelcomeLetterGroup2 = (role) =>
  WELCOME_LETTER_GROUP_2.includes(role);

/** MSW uses the contractor agreement UI plus Group 1 supervisor fields. */
export const isWelcomeLetterContractorForm = (role) =>
  isWelcomeLetterGroup2(role) || role === "MSW";

export const toApiIsoDateTime = (value) => toIsoDate(value) || "";

const cleanInt = (val, fallback = 1) => {
  const parsed = parseInt(val, 10);
  if (!Number.isNaN(parsed)) return parsed;
  const fb = parseInt(fallback, 10);
  return Number.isNaN(fb) ? 1 : fb;
};

export const extractWelcomeLetterPayRate = (data = {}) => {
  const nested = data.payRate && typeof data.payRate === "object" ? data.payRate : {};
  return PAY_RATE_KEYS.reduce((acc, key) => {
    acc[key] = nested[key] ?? data[`payRate.${key}`] ?? "";
    return acc;
  }, {});
};

/**
 * Hydrate RHF values from a welcome-letter API record so edit/reopen
 * refills contractor fields, pay rates, addresses, and dates.
 */
export const mapWelcomeLetterToForm = (data = {}) => {
  const payRate = extractWelcomeLetterPayRate(data);
  const payRateFields = PAY_RATE_KEYS.reduce((acc, key) => {
    acc[`payRate.${key}`] = payRate[key] ?? "";
    return acc;
  }, {});

  const parsedName =
    data.name ||
    data.applicantName ||
    data.employeeSignature?.name ||
    data.employeeSignature?.employee_name ||
    "";

  const hireDisplay = parseIsoDateToMDYUS(data.hireDate);

  return {
    id: data.id,
    tenantId: data.tenantId,
    userId: data.userId,
    status: data.status || "",
    comments: data.comments || "",
    payRate,
    ...payRateFields,
    hireDate: hireDisplay,
    effectiveDate: hireDisplay,
    firstPatientCareDate: parseIsoDateToMDYUS(data.firstPatientCareDate),
    contractorName: parsedName,
    employeeName: parsedName,
    applicantName: parsedName,
    supervisorName: data.supervisor || "",
    supervisor: data.supervisor || "",
    contractorAddress:
      data.agencyDetails?.contractorAddress || data.contractorAddress || "",
    agencyAddress:
      data.agencyDetails?.agencyAddress || data.agencyAddress || "",
    applicantSignature:
      data.applicantSignature ||
      data.empSignature ||
      data.employeeSignature?.signature ||
      "",
    applicantSignedAt: parseIsoDateToMDYUS(
      data.applicantSignedAt ||
        data.empSignedAt ||
        data.employeeSignature?.date ||
        "",
    ),
    applicantStatementAccepted: Boolean(data.applicantStatementAccepted),
    reviewedBySignature:
      data.reviewedBySignature || data.agencyDetails?.signature || "",
    reviewedBySignedBy:
      data.reviewedBySignedBy || data.agencyDetails?.name || "",
    reviewedBySignedAt: parseIsoDateToMDYUS(data.reviewedBySignedAt || ""),
  };
};

/**
 * Build create/update payload. Admin signature date is omitted unless set;
 * it is required (and always sent) only when flag is `completed`.
 */
export const buildWelcomeLetterPayload = ({
  data = {},
  flag,
  additionalData = {},
  role,
  fallbackTenantId,
  fallbackUserId,
  reviewerUserId,
} = {}) => {
  const merged = mergeFormDataWithStatusExtras(data, additionalData);
  const isGroup1 = isWelcomeLetterGroup1(role);
  const isContractor = isWelcomeLetterContractorForm(role);

  const payRate = extractWelcomeLetterPayRate(merged);
  const tId = cleanInt(merged.tenantId, fallbackTenantId);
  const uId = cleanInt(merged.userId, fallbackUserId);

  const applicantSignedAt =
    toApiIsoDateTime(merged.applicantSignedAt) || new Date().toISOString();
  const reviewedBySignedAt = toApiIsoDateTime(
    merged.reviewedBySignedAt || merged.adminSignatureDate,
  );

  const payload = {
    tenantId: tId,
    userId: uId,
    role,
    name:
      merged.contractorName ||
      merged.employeeName ||
      merged.name ||
      merged.applicantName ||
      "",
    payRate,
    applicantSignature: merged.applicantSignature || "",
    applicantSignedAt,
    applicantName:
      merged.applicantName ||
      merged.contractorName ||
      merged.employeeName ||
      "",
    reviewedBySignature: merged.reviewedBySignature || "",
    reviewedBySignedBy: merged.reviewedBySignedBy || "",
    ack_count: isContractor ? 13 : isGroup1 ? 10 : undefined,
    comments: merged.comments || "",
    flag,
    applicantStatementAccepted: merged.applicantStatementAccepted,
  };

  if (flag === "completed") {
    payload.reviewedBySignedAt =
      reviewedBySignedAt || new Date().toISOString();
  } else if (reviewedBySignedAt) {
    payload.reviewedBySignedAt = reviewedBySignedAt;
  }

  if (isGroup1) {
    payload.supervisor = merged.supervisorName || merged.supervisor || "";
    const firstCare = toApiIsoDateTime(merged.firstPatientCareDate);
    if (firstCare) payload.firstPatientCareDate = firstCare;
    payload.empSignature = merged.applicantSignature || "";
    payload.empSignedAt = applicantSignedAt;
    payload.empSignedByUserId = uId;
  }

  if (isContractor) {
    payload.agencyDetails = {
      agency_name:
        merged.agencyDetails?.agency_name || "Infinite Home Health, Inc.",
      name: merged.reviewedBySignedBy || merged.agencyDetails?.name || "",
      title: merged.agencyDetails?.title || "Administrator",
      signature:
        merged.reviewedBySignature || merged.agencyDetails?.signature || "",
      contractorAddress:
        merged.contractorAddress ||
        merged.agencyDetails?.contractorAddress ||
        "",
      agencyAddress:
        merged.agencyAddress || merged.agencyDetails?.agencyAddress || "",
    };
    payload.employeeSignature = {
      employee_name: merged.contractorName || merged.employeeName || "",
      name: merged.contractorName || merged.employeeName || "",
      title: role,
      signature: merged.applicantSignature || "",
      date: applicantSignedAt,
    };
  }

  const hireIso = toApiIsoDateTime(merged.hireDate || merged.effectiveDate);
  if (hireIso) payload.hireDate = hireIso;

  if (merged.reviewedBySignature && reviewerUserId) {
    payload.reviewedByUserId = cleanInt(reviewerUserId);
  }

  return payload;
};
